import React, {useRef} from 'react';
import {classes, style} from './doppe-action-type-widget-item-list.st.css';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {
    ButtonType,
    IconButton,
    ListView,
    ListViewDataSourceDataItem,
    ListViewItemRoot,
    NavigationOrientation,
    reactCreateMemo,
    ScrollView,
    ScrollViewEdgeDisabled,
    ScrollViewEdgeDisabledSide,
    ScrollViewScrollbars,
    ScrollViewScrollingDirection,
    Swiper,
    SwiperNavigationListView,
    useDynamicRef,
    useSwiperNavigation,
    useWindowDependantMemo
} from '@wix/devzai-utils-react';
import IconChevronLeft
    from '../../../components/doppe-action-button-templates/doppe-standard-action-button-template/icons/ChevronLeft.svg';
import IconChevronRight
    from '../../../components/doppe-action-button-templates/doppe-standard-action-button-template/icons/ChevronRight.svg';
import {
    DoppeViewerLangDictionary,
    useDoppViewerIsRtl
} from '../../../components/doppe-viewer-lang-dictionary/doppe-viewer-lang-dictionary';
import {domGetScrollbarSize} from '@wix/devzai-utils-dom';
import {
    DoppeActionTypeWidgetInnerButton
} from '../../../components/doppe-action-type-widget-inner-button/doppe-action-type-widget-inner-button';
import {IDoppeViewerActionContext, useBymoPageContextProps} from '../../../doppe-sdk';
import {arraySkipTake, evaluateFunction, HtmlObjectResizeMode, ObservableValue} from '@wix/devzai-utils-common';
import {
    DoppeActionLayout,
    DoppeActionListItemClickBehaviour,
    DoppeHideableValue,
    doppeHideableValueCreateHidden,
    doppeHideableValueIsVisibleAndNotEqualValue,
    doppePremiumFeatureGetLimit,
    DoppePremiumFeatures
} from '../../../client-server-common';
import {WixMediaResource} from '@wix/devzai-common-wix';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapper
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper/doppe-action-type-widget-content-layout-new-wrapper';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {DoppePageMediaView} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {DoppeViewerActionContext} from '../../../components/doppe-viewer-action-context/doppe-viewer-action-context';
import {
    doppeActionWidgetPerformWidgetContentReplacement
} from '../../../components/doppe-action-widget-utils/doppe-action-widget-utils';
import {DoppeActionWidgetPopupMode} from '../../../client-server-common/types/doppe-action-widget-popup-mode';
import {
    DoppeViewerModalPanel,
    DoppeViewerModalPanelLayout
} from '../../../components/doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {DoppeCouponPopup} from '../../../components/doppe-coupon-popuop/doppe-coupon-popup';
import {
    DoppeViewerModalPanelHeader
} from '../../../components/doppe-viewer-modal-panel-header/doppe-viewer-modal-panel-header';

export const DoppeActionTypeWidgetItemList = reactCreateMemo(function DoppeActionTypeWidgetItemList<T>(props: DoppeActionTypeWidgetItemList.Props<T>) {
    const {
        dataSourceItems,
        layout,
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();

    const limit = doppePremiumFeatureGetLimit(
        bymoPageContextProps.premiumFeaturesStatus, DoppePremiumFeatures.hopp_add_items_to_list_action);

    const normalizedProps = {
        ...props,
        dataSourceItems: arraySkipTake(dataSourceItems, 0, limit)
    }


    switch (layout) {
        case 'Swiper': {
            return <DoppeActionTypeWidgetItemListSwiper
                {...normalizedProps}
            />
        }
        case 'CarouselSmall': {
            return <DoppeActionTypeWidgetItemListCarousel
                {...normalizedProps}
            />
        }
        case 'Carousel': {
            return <DoppeActionTypeWidgetItemListCarousel
                {...normalizedProps}
            />
        }
        case 'Column': {
            return <DoppeActionTypeWidgetItemListNormal
                {...normalizedProps}
            />
        }
        case 'Grid': {
            return <DoppeActionTypeWidgetItemListNormal
                {...normalizedProps}
            />
        }
        case 'ImagesGrid': {
            return <DoppeActionTypeWidgetItemListNormal
                {...normalizedProps}
            />
        }
    }


});

const DoppeActionTypeWidgetItemListCarousel = reactCreateMemo(function DoppeActionTypeWidgetItemListCarousel<T>(props: DoppeActionTypeWidgetItemList.Props<T>) {
    const {
        className,
        layout,
        onItemClick,
        renderItem,
        dataSourceItems,
        actionContext,
        hasSeeMoreButton,
        noPadding = false,
        media,
        listTitle = doppeHideableValueCreateHidden(''),
        ...htmlAttributes
    } = props;

    const actionSettings = actionContext.action.settings;

    const {
        showButton,
        buttonUrl,
        buttonText,
    } = actionSettings;

    const scrollViewRef = useRef<ScrollView>(null);
    const isRtl = useDoppViewerIsRtl();
    const scrollbarSize = useWindowDependantMemo(() => domGetScrollbarSize(), []) ?? 0;


    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapper
            {...htmlAttributes}
            noContentPadding={true}
            mainMedia={media ? (
                <DoppePageMediaView
                    mediaResource={media}
                    layoutSpec={{autoHeight: true, maxHeight: 360}}
                    widthSpec={{contentRelativeWidth: 1}}
                    mediaResizeMode={HtmlObjectResizeMode.Cover}
                />
            ) : null}
            fullWidthMainMedia={true}
            footer={showButton ? (
                <DoppeActionTypeWidgetInnerButton
                    className={style(classes.linkButton)}
                    buttonType={ButtonType.NormalButton}
                    tagName={'a'}
                    href={buttonUrl}
                    target={'_blank'}
                    onClick={() => {
                        actionContext.reportGoalAchieved();
                    }}
                >
                    {buttonText}
                </DoppeActionTypeWidgetInnerButton>
            ) : null}
            className={style(classes.root, {
                hasOneItem: dataSourceItems.length === 1,
                noPadding: noPadding,
                hasTitle: doppeHideableValueIsVisibleAndNotEqualValue(listTitle, ''),
                hasSeeMoreButton: hasSeeMoreButton,
            }, FlexLayout.column.default, className)}
        >
            <div>
                <DoppeActionTypeWidgetInnerTitle
                    className={style(classes.listTitle, {})}
                >
                    {listTitle}
                </DoppeActionTypeWidgetInnerTitle>
                <ScrollView
                    className={style(classes.itemsScrollView, FlexLayout.noShrink, FlexLayout.fillRemainingVerticalSpace)}
                    ref={scrollViewRef}
                    scrollingDirection={ScrollViewScrollingDirection.Horizontal}
                    scrollbars={ScrollViewScrollbars.Hidden}
                    // scrolledContentWrapperClassName={style(classes.contentListViewContainer)}
                    fixedContent={scrollbarSize > 0 && dataSourceItems.length > 1 ? (
                        <>
                            <ScrollViewEdgeDisabled
                                scrollViewRef={scrollViewRef}
                                side={ScrollViewEdgeDisabledSide.Left}
                                className={style(classes.leftEdge, FlexLayout.centerContent)}
                                isRtl={isRtl}
                            >
                                {
                                    (isDisabled, hasNoScrolling) => {
                                        return hasNoScrolling ? <div/> : <IconButton
                                            className={style(classes.arrowScrollButton, {isDisabled: isDisabled})}
                                            icon={!isRtl ? IconChevronLeft : IconChevronRight}
                                            onClick={() => {
                                                if (!isDisabled) {
                                                    const scrollView = scrollViewRef.current;
                                                    if (scrollView) {
                                                        scrollView.getScrolledContentElement().scrollBy({
                                                            behavior: 'smooth',
                                                            left: -scrollView.getScrollViewElement().offsetWidth / 1.5 * (isRtl ? -1 : 1)
                                                        })
                                                    }
                                                }
                                            }}
                                        />
                                    }
                                }
                            </ScrollViewEdgeDisabled>
                            <ScrollViewEdgeDisabled
                                scrollViewRef={scrollViewRef}
                                side={ScrollViewEdgeDisabledSide.Right}
                                className={style(classes.rightEdge, FlexLayout.centerContent)}
                                isRtl={isRtl}
                            >
                                {
                                    (isDisabled, hasNoScrolling) => {
                                        return hasNoScrolling ? <div/> : <IconButton
                                            className={style(classes.arrowScrollButton, {isDisabled: isDisabled})}
                                            icon={!isRtl ? IconChevronRight : IconChevronLeft}
                                            onClick={() => {
                                                if (!isDisabled) {
                                                    const scrollView = scrollViewRef.current;
                                                    if (scrollView) {
                                                        scrollView.getScrolledContentElement().scrollBy({
                                                            behavior: 'smooth',
                                                            left: scrollView.getScrollViewElement().offsetWidth / 1.5 * (isRtl ? -1 : 1)
                                                        })
                                                    }
                                                }
                                            }}
                                        />
                                    }
                                }
                            </ScrollViewEdgeDisabled>
                        </>
                    ) : null}
                >
                    <ListView<T>
                        orientation={NavigationOrientation.Horizontal}
                        isFocusable={false}
                        className={style(classes.productsItemsListView, {
                            layout: layout,
                        })}
                        onItemClick={eventData => {
                            onItemClick(eventData.dataItem);
                        }}
                        renderItem={(renderProps) => {
                            return renderItem(renderProps.dataItem, layout, renderProps.itemIndex);
                        }}
                    >
                        {dataSourceItems}
                    </ListView>
                </ScrollView>
            </div>
        </DoppeActionTypeWidgetContentLayoutNewWrapper>
    );
});

const DoppeActionTypeWidgetItemListNormal = reactCreateMemo(function DoppeActionTypeWidgetItemListNormal<T>(props: DoppeActionTypeWidgetItemList.Props<T>) {
    const {
        className,
        layout,
        onItemClick,
        renderItem,
        dataSourceItems,
        actionContext,
        hasSeeMoreButton,
        noPadding = false,
        media,
        listTitle = doppeHideableValueCreateHidden(''),
        ...htmlAttributes
    } = props;

    const actionSettings = actionContext.action.settings;

    const {
        showButton,
        buttonUrl,
        buttonText,
    } = actionSettings;

    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapper
            {...htmlAttributes}
            noContentPadding={true}
            mainMedia={media ? (
                <DoppePageMediaView
                    mediaResource={media}
                    layoutSpec={{autoHeight: true, maxHeight: 360}}
                    widthSpec={{contentRelativeWidth: 1}}
                    mediaResizeMode={HtmlObjectResizeMode.Cover}
                />
            ) : null}
            fullWidthMainMedia={true}
            footer={showButton ? (
                <DoppeActionTypeWidgetInnerButton
                    className={style(classes.linkButton)}
                    buttonType={ButtonType.NormalButton}
                    tagName={'a'}
                    href={buttonUrl}
                    target={'_blank'}
                    onClick={() => {
                        actionContext.reportGoalAchieved();
                    }}
                >
                    {buttonText}
                </DoppeActionTypeWidgetInnerButton>
            ) : null}
            className={style(classes.root, {
                hasOneItem: dataSourceItems.length === 1,
                noPadding: noPadding,
                hasTitle: doppeHideableValueIsVisibleAndNotEqualValue(listTitle, ''),
                hasSeeMoreButton: hasSeeMoreButton,
            }, className, FlexLayout.column.default)}
        >
            <div
                className={style(classes.listContent, {}, FlexLayout.column.default, FlexLayout.fillRemainingVerticalSpace)}
            >
                <DoppeActionTypeWidgetInnerTitle
                    className={style(classes.listTitle, {})}
                >
                    {listTitle}
                </DoppeActionTypeWidgetInnerTitle>
                <ListView<T>
                    orientation={NavigationOrientation.Vertical}
                    isFocusable={false}
                    className={style(classes.productsItemsListView, {
                        layout: layout,
                    }, FlexLayout.fillRemainingVerticalSpace)}
                    onItemClick={eventData => {
                        onItemClick(eventData.dataItem);
                    }}
                    renderItem={(renderProps) => {
                        return renderItem(renderProps.dataItem, layout, renderProps.itemIndex);
                    }}
                >
                    {dataSourceItems}
                </ListView>
            </div>
        </DoppeActionTypeWidgetContentLayoutNewWrapper>
    );
});


const DoppeActionTypeWidgetItemListSwiper = reactCreateMemo(function DoppeActionTypeWidgetItemListCarousel<T>(props: DoppeActionTypeWidgetItemList.Props<T>) {
    const {
        className,
        layout,
        onItemClick,
        renderItem,
        dataSourceItems,
        actionContext,
        hasSeeMoreButton,
        noPadding = false,
        media,
        listTitle = doppeHideableValueCreateHidden(''),
        ...htmlAttributes
    } = props;

    const actionSettings = actionContext.action.settings;

    const {
        showButton,
        buttonUrl,
        buttonText,
    } = actionSettings;

    const isRtl = useDoppViewerIsRtl();

    const swiperRef = useDynamicRef<HTMLDivElement>();
    const swiper = useSwiperNavigation(swiperRef);

    const scrollbarSize = useWindowDependantMemo(() => domGetScrollbarSize(), []) ?? 0;

    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapper
            {...htmlAttributes}
            noContentPadding={true}
            mainMedia={media ? (
                <DoppePageMediaView
                    mediaResource={media}
                    layoutSpec={{autoHeight: true, maxHeight: 360}}
                    widthSpec={{contentRelativeWidth: 1}}
                    mediaResizeMode={HtmlObjectResizeMode.Cover}
                />
            ) : null}
            fullWidthMainMedia={true}
            footer={showButton ? (
                <DoppeActionTypeWidgetInnerButton
                    className={style(classes.linkButton)}
                    buttonType={ButtonType.NormalButton}
                    tagName={'a'}
                    href={buttonUrl}
                    target={'_blank'}
                    onClick={() => {
                        actionContext.reportGoalAchieved();
                    }}
                >
                    {buttonText}
                </DoppeActionTypeWidgetInnerButton>
            ) : null}
            className={style(classes.root, {
                hasOneItem: dataSourceItems.length === 1,
                noPadding: noPadding,
                hasTitle: doppeHideableValueIsVisibleAndNotEqualValue(listTitle, ''),
                hasSeeMoreButton: hasSeeMoreButton,
            }, FlexLayout.column.default, className)}
        >
            <div
                className={style(classes.swiperContainer, {}, FlexLayout.fillRemainingVerticalSpace)}
            >
                <DoppeActionTypeWidgetInnerTitle
                    className={style(classes.listTitle, {})}
                >
                    {listTitle}
                </DoppeActionTypeWidgetInnerTitle>
                <Swiper
                    ref={swiperRef}
                    visibleItemsCount={1}
                    className={style(classes.productsItemsListView, {
                        layout: layout,
                    })}
                >
                    {
                        dataSourceItems.map((dataSourceItem, index) => {
                            return <div
                                className={style(classes.slide, {})}
                                key={dataSourceItem.id}
                                onClick={() => {
                                    onItemClick(dataSourceItem.dataItem);
                                }}
                            >
                                {renderItem(dataSourceItem.dataItem, layout, index)}
                            </div>
                        })
                    }
                </Swiper>
                {
                    scrollbarSize > 0 && swiper.currentSlide !== undefined && swiper.slidesCount > 1 ?
                        <>
                            <IconButton
                                className={style(classes.arrowScrollButton, {
                                    isDisabled: swiper.currentSlide === 0
                                }, classes.leftEdge)}
                                icon={isRtl ? IconChevronRight : IconChevronLeft}
                                onClick={() => {
                                    if (swiper.currentSlide !== undefined) {
                                        const targetElement = swiperRef.current?.children?.item(Math.max(swiper.currentSlide - 1, 0));

                                        if (targetElement) {
                                            swiperRef.current?.scrollTo({
                                                left: evaluateFunction(() => {
                                                    const previousSibling = targetElement.previousElementSibling as HTMLElement | null;
                                                    if (previousSibling) {
                                                        return previousSibling.offsetLeft + previousSibling.offsetWidth * (isRtl ? -1 : 1);
                                                    } else {
                                                        return 0;
                                                    }
                                                }),
                                                behavior: 'smooth'
                                            })
                                        }
                                    }

                                }}
                            />
                            <IconButton
                                className={style(classes.arrowScrollButton, {
                                    isDisabled: swiper.currentSlide === swiper.slidesCount - 1
                                }, classes.rightEdge)}
                                icon={!isRtl ? IconChevronRight : IconChevronLeft}
                                onClick={() => {
                                    if (swiper.currentSlide !== undefined) {
                                        const targetElement = swiperRef.current?.children?.item(Math.min(swiper.currentSlide + 1, swiper.slidesCount - 1));

                                        if (targetElement) {
                                            swiperRef.current?.scrollTo({
                                                left: evaluateFunction(() => {
                                                    const previousSibling = targetElement.previousElementSibling as HTMLElement | null;
                                                    if (previousSibling) {
                                                        return previousSibling.offsetLeft + previousSibling.offsetWidth * (isRtl ? -1 : 1);
                                                    } else {
                                                        return 0;
                                                    }
                                                }),
                                                behavior: 'smooth'
                                            })
                                        }
                                    }

                                }}
                            />
                        </>
                        :
                        null
                }
            </div>
            {
                swiper.slidesCount > 1 ?
                    <SwiperNavigationListView
                        className={style(classes.swiperNavigationListView, FlexLayout.row.alignToCenter)}
                        swiperElementRef={swiperRef}
                        isRtl={isRtl}
                        renderItem={(renderProps) => {
                            return (
                                <ListViewItemRoot
                                    className={style(classes.navigationMenuItem, {isActive: renderProps.isActive}, CommonClasses.button)}
                                />
                            )
                        }}
                    />
                    :
                    null
            }
        </DoppeActionTypeWidgetContentLayoutNewWrapper>
    );
});


export namespace DoppeActionTypeWidgetItemList {

    export interface Props<T> extends React.HTMLAttributes<any> {
        layout: DoppeActionLayout;
        hasSeeMoreButton: boolean;
        onItemClick: (item: T) => void;
        renderItem: (item: T, layout: DoppeActionLayout, index: number) => React.ReactElement;
        dataSourceItems: ListViewDataSourceDataItem<T>[];
        actionContext: IDoppeViewerActionContext<any>;
        noPadding?: boolean;
        media?: WixMediaResource | null;
        listTitle?: DoppeHideableValue<string>,
    }
}


export function onListItemClick(options: {
    itemClickBehaviour: DoppeActionListItemClickBehaviour;
    renderingInnerStateController: ObservableValue<boolean>;
    observableCurrentItemIdToRender?: ObservableValue<string | null>;
    rootElementRef: React.RefObject<any>;
    actionContext: DoppeViewerActionContext<any>;
    itemId: string;
    itemIndex: number;
    itemLink?: string;
    couponCode?: DoppeHideableValue<string>;
    viewerLang: DoppeViewerLangDictionary;
}) {

    const {
        itemClickBehaviour,
        itemId,
        itemIndex,
        itemLink,
        actionContext,
        rootElementRef,
        observableCurrentItemIdToRender,
        renderingInnerStateController,
        couponCode,
        viewerLang
    } = options;

    switch (itemClickBehaviour) {
        case DoppeActionListItemClickBehaviour.PreviewItem: {
            doppeActionWidgetPerformWidgetContentReplacement(
                rootElementRef.current,
                () => {
                    renderingInnerStateController.setValue(true);
                    observableCurrentItemIdToRender?.setValue(itemId)
                }
            )
            break;
        }
        case DoppeActionListItemClickBehaviour.PreviewItemAsPopup: {
            actionContext.previewAction(actionContext.action.id, {
                listItemId: itemId,
                widgetPopupMode: DoppeActionWidgetPopupMode.Drawer,
                actionIndex: actionContext.getActionIndex(),
            })
            break;
        }
        case DoppeActionListItemClickBehaviour.OpenLink: {
            if (itemLink) {
                if (couponCode && doppeHideableValueIsVisibleAndNotEqualValue(couponCode, '')) {
                    actionContext.modalsController.showInnerModal({
                        content: ({modalController}) => (
                            <DoppeViewerModalPanel
                                className={style(classes.couponModal, FlexLayout.column.default)}
                                mobileLayout={DoppeViewerModalPanelLayout.FullScreen}
                                desktopLayout={DoppeViewerModalPanelLayout.FullScreen}
                            >
                                <DoppeViewerModalPanelHeader
                                    title={''}
                                    dismissButtonCallback={() => modalController.closeWindow()}
                                />
                                <div
                                    className={style(classes.couponContainer, {}, FlexLayout.fillRemainingVerticalSpace, FlexLayout.centerContent)}
                                >
                                    <DoppeCouponPopup
                                        className={style(classes.couponPopup, {})}
                                        couponCode={couponCode}
                                        couponUrl={itemLink}
                                        linkId={actionContext.action.id}
                                        onLinkClicked={() => {
                                            actionContext.reportGoalAchieved(itemId, itemIndex);
                                            actionContext.navigateToUrl(itemLink, true);
                                        }}
                                        onClose={()=>{
                                            modalController.closeWindow();
                                        }}
                                        showBackToButton={viewerLang.backToPage}
                                        showCloseButton={false}
                                        couponDescription={viewerLang.couponDescription}
                                        couponTitle={viewerLang.copyCouponTitle}
                                    />
                                </div>
                            </DoppeViewerModalPanel>
                )
                })
                } else {
                    actionContext.reportGoalAchieved(itemId, itemIndex);
                    actionContext.navigateToUrl(itemLink, true);
                }
            }

            break;
        }
    }
}

